/**
 * ProjectDetailsCard — IDI-169: collapsible project details section.
 *
 * Fields: Job Type (required), Measurement Method (required), Lead Source (required).
 * Collapses to a summary card after all required fields are filled.
 * Edit button reopens it.
 *
 * Uses SelectMenu (existing atom) for all three dropdowns.
 * Options sourced from useSettingsContext().getOptions().
 *
 * IDI-240: Project name field moved into this card as its FIRST field
 * (was a bare <Input> sibling above the card at QuoteBuilderPage.js).
 * Project name is optional (Ruling 3) and NOT part of the allFilled collapse
 * predicate (Ruling 2 — gating collapse on an optional field would make it
 * de-facto required with no affordance). In the collapsed summary the name
 * row is omitted entirely when empty (Ruling 4 — a labelled blank row reads
 * as a rendering bug).
 */

import React, { useState } from 'react';
import { useSettingsContext } from '../../../hooks/useSettings';
import SelectMenu from '../../ui/SelectMenu';
import { Input } from '../../ui/Input';
import { ChevronUpIcon, PencilIcon } from '@heroicons/react/24/outline';

const MEASUREMENT_OPTIONS = [
  { value: 'blueprint', label: 'Blueprint' },
  { value: 'field', label: 'Field' },
];

export default function ProjectDetailsCard({
  prefill,
  onPrefillChange,
  projectTitle = '',
  onProjectTitleChange = () => {},
}) {
  const { getOptions, getEnumLabel } = useSettingsContext();
  const jobTypeOptions = getOptions('job_types').map(o => ({ value: o.value || o.id || o, label: o.label || o.name || o }));
  const leadSourceOptions = getOptions('lead_sources').map(o => ({ value: o.value || o.id || o, label: o.label || o.name || o }));

  // Collapsed when all three fields are filled
  const allFilled = prefill.jobTypeId && prefill.measurementMethod && prefill.leadSource;
  const [editing, setEditing] = useState(!allFilled);

  const update = (field, value) => {
    onPrefillChange(prev => ({ ...prev, [field]: value }));
  };

  // IDI-178: legacy carried a per-quote "Round Materials" Yes/No toggle
  // (Quote_RoundMaterials, migrated onto quotes as `roundMaterials` --
  // confirmed live, ~48% of tenant 270's quotes carry it) that BIDIT 2.0
  // never surfaced anywhere -- the field existed on the record but nothing
  // read or wrote it. Placed here (a quote-level metadata card already
  // wired to prefill/onPrefillChange) rather than inventing a new card.
  //
  // Placeholder rounding semantics (2026-08-31, pending real coverage-rate
  // data): rounds the entered sqft up to the next whole square foot before
  // pricing. The legacy behaviour (rounding material QUANTITY to a whole
  // coverage unit, e.g. whole bags/rolls) needs real per-product coverage
  // data to implement correctly -- that data isn't consumed anywhere in
  // the live quote calculation today (see quote-builder/seedCatalog.js;
  // coverage_per_set/coverage_rvalues_* only exist in the Settings admin
  // editor). This toggle's PERSISTENCE and UI are the real deliverable
  // here; the rounding RULE is an explicit placeholder to be replaced once
  // that data exists -- see quote-builder/roundMaterials.js.
  const roundMaterials = prefill.roundMaterials === true;

  const toggleRoundMaterials = () => {
    onPrefillChange(prev => ({ ...prev, roundMaterials: !roundMaterials }));
  };

  // Resolve jobTypeId to a matching option value (migrated records store integer IDs)
  //
  // IDI-169: a migrated quote's jobTypeId is a raw legacy integer id into
  // the standalone JOBTYPE# entities (a DIFFERENT system from
  // settings.jobTypes[], the tenant-configurable dropdown list
  // jobTypeOptions is built from). It will almost never equal any option's
  // `value` (a name-slug), so the select rendered a permanent placeholder
  // for every migrated quote despite jobTypeId being perfectly resolvable
  // server-side. getQuote now resolves it into `jobTypeLabel` (mirrors the
  // existing lead_source/payment_term enrichment pattern) -- when the id
  // doesn't match a real dropdown option, inject a synthetic one from that
  // label so the select shows the REAL name (not a placeholder) and stays
  // selectable/editable rather than silently blank.
  const matchedJobTypeOption = jobTypeOptions.find(o => o.value === prefill.jobTypeId);
  const jobTypeOptionsWithMigrated = (matchedJobTypeOption || !prefill.jobTypeId || !prefill.jobTypeLabel)
    ? jobTypeOptions
    : [{ value: prefill.jobTypeId, label: prefill.jobTypeLabel }, ...jobTypeOptions];
  const resolvedJobType = prefill.jobTypeId
    ? ((matchedJobTypeOption || prefill.jobTypeLabel) ? prefill.jobTypeId : '')
    : '';

  if (!editing && allFilled) {
    // Summary card (collapsed)
    const jobLabel = jobTypeOptions.find(o => o.value === prefill.jobTypeId)?.label
      || prefill.jobTypeLabel || getEnumLabel('job_types', prefill.jobTypeId) || prefill.jobTypeId;
    const measLabel = MEASUREMENT_OPTIONS.find(o => o.value === prefill.measurementMethod)?.label || prefill.measurementMethod;
    const leadLabel = leadSourceOptions.find(o => o.value === prefill.leadSource)?.label
      || getEnumLabel('lead_sources', prefill.leadSource) || prefill.leadSource;

    return (
      <div className="card p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Project Details</h3>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
          >
            <PencilIcon className="h-3.5 w-3.5" /> Edit
          </button>
        </div>
        {projectTitle && projectTitle.trim() && (
          <div className="mb-3 text-sm text-gray-600">
            <span className="text-gray-400 text-xs block">Project name</span>{projectTitle}
          </div>
        )}
        <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
          <div><span className="text-gray-400 text-xs block">Job Type</span>{jobLabel}</div>
          <div><span className="text-gray-400 text-xs block">Measurement</span>{measLabel}</div>
          <div><span className="text-gray-400 text-xs block">Lead Source</span>{leadLabel}</div>
        </div>
        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={roundMaterials}
              onChange={toggleRoundMaterials}
              className="rounded border-[color:var(--color-border)]"
              style={{ accentColor: 'var(--color-action-primary)' }}
            />
            Round Materials
          </label>
        </div>
      </div>
    );
  }

  // Expanded (editing)
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Project Details</h3>
        {allFilled && (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            <ChevronUpIcon className="h-4 w-4 inline" /> Collapse
          </button>
        )}
      </div>
      <div className="mb-4">
        <Input
          label="Project name"
          placeholder="e.g. Kitchen Remodel, Smith Residence"
          value={projectTitle}
          onChange={onProjectTitleChange}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SelectMenu
          label="Job Type"
          required
          value={resolvedJobType || prefill.jobTypeId}
          onChange={(val) => update('jobTypeId', val)}
          options={jobTypeOptionsWithMigrated}
          placeholder="Select job type…"
        />
        <SelectMenu
          label="How was this measured?"
          required
          value={prefill.measurementMethod}
          onChange={(val) => update('measurementMethod', val)}
          options={MEASUREMENT_OPTIONS}
          placeholder="Select…"
        />
        <SelectMenu
          label="Lead Source"
          required
          value={prefill.leadSource}
          onChange={(val) => update('leadSource', val)}
          options={leadSourceOptions}
          placeholder="Select lead source…"
        />
      </div>
      <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={roundMaterials}
            onChange={toggleRoundMaterials}
            className="rounded border-[color:var(--color-border)]"
            style={{ accentColor: 'var(--color-action-primary)' }}
          />
          Round Materials
        </label>
        <p className="mt-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          When on, material quantities round up before pricing. Defaults to off.
        </p>
      </div>
    </div>
  );
}
